import { create } from "zustand";
import type { EnrichedArtifact } from "src/reusable/Chat/PromptInput/ArtifactSelectorModal";
import type { Dataset } from "src/reusable/Chat/PromptInput/SelectedDatasetsContainer";

export type RfpFileState = {
  file?: File;
  name: string;
  id?: number;
  size: number;
};

// Phase A only today. Phase B (source resolution) and Phase C (validation
// scoring) are designed but not yet implemented, so only PENDING and
// IDENTIFIED statuses are reachable.
export type ClaimStatus = "pending" | "identified";

export interface ClaimState {
  id: number;
  text: string;
  page_number: number | null;
  source_citation: string | null;
  source_chunk_fk: number | null;
  score: number | null;
  rationale: string | null;
  status: ClaimStatus;
}

type ValidationStatus = "idle" | "identifying" | "identified" | "error";

type State = {
  vnvRunId: number | undefined;
  dialogueId: number | undefined;
  dialogueCreationPromise: Promise<number> | null;
  selectedArtifact: EnrichedArtifact | null;
  inputFile: RfpFileState | null;
  selectedDatasets: Dataset[];
  selectedDatasetIds: number[];
  claims: ClaimState[];
  activeClaim: ClaimState | null;
  validationStatus: ValidationStatus;
  validationError: string | null;
  identificationTotalChunks: number | null;
  identificationChunksCompleted: number | null;
};

type Action = {
  setVnvRunId: (id: number | undefined) => void;
  setDialogueId: (id: number | undefined) => void;
  setDialogueCreationPromise: (promise: Promise<number> | null) => void;
  getOrCreateDialogue: (createFn: () => Promise<number>) => Promise<number>;
  setSelectedArtifact: (artifact: EnrichedArtifact | null) => void;
  setInputFile: (file: RfpFileState | null) => void;
  setSelectedDatasets: (datasets: Dataset[]) => void;
  setSelectedDatasetIds: (ids: number[]) => void;
  setClaims: (claims: ClaimState[]) => void;
  addClaim: (claim: ClaimState) => void;
  addClaims: (claims: ClaimState[]) => void;
  updateClaim: (id: number, updates: Partial<ClaimState>) => void;
  removeClaim: (id: number) => void;
  setActiveClaim: (claim: ClaimState | null) => void;
  setValidationStatus: (status: ValidationStatus) => void;
  setValidationError: (error: string | null) => void;
  setIdentificationTotalChunks: (total: number | null) => void;
  setIdentificationChunksCompleted: (completed: number | null) => void;
  reset: () => void;
};

const initialState: State = {
  vnvRunId: undefined,
  dialogueId: undefined,
  dialogueCreationPromise: null,
  selectedArtifact: null,
  inputFile: null,
  selectedDatasets: [],
  selectedDatasetIds: [],
  claims: [],
  activeClaim: null,
  validationStatus: "idle",
  validationError: null,
  identificationTotalChunks: null,
  identificationChunksCompleted: null,
};

export const useVnvAssistStore = create<State & Action>()((set, get) => ({
  ...initialState,

  setVnvRunId: (id) => set({ vnvRunId: id }),
  setDialogueId: (id) => set({ dialogueId: id }),
  setDialogueCreationPromise: (promise) =>
    set({ dialogueCreationPromise: promise }),

  getOrCreateDialogue: async (createFn) => {
    const state = get();
    if (state.dialogueId) return state.dialogueId;
    if (state.dialogueCreationPromise) return state.dialogueCreationPromise;

    const promise = createFn()
      .then((id) => {
        set({ dialogueId: id, dialogueCreationPromise: null });
        return id;
      })
      .catch((error) => {
        set({ dialogueCreationPromise: null });
        throw error;
      });

    set({ dialogueCreationPromise: promise });
    return promise;
  },

  setSelectedArtifact: (artifact) => set({ selectedArtifact: artifact }),
  setInputFile: (file) => set({ inputFile: file }),
  setSelectedDatasets: (datasets) =>
    set({
      selectedDatasets: datasets,
      selectedDatasetIds: datasets.map((d) => d.id),
    }),
  setSelectedDatasetIds: (ids) => set({ selectedDatasetIds: ids }),
  setClaims: (claims) => set({ claims }),
  addClaim: (claim) => set((state) => ({ claims: [...state.claims, claim] })),
  addClaims: (claims) =>
    set((state) =>
      claims.length === 0 ? state : { claims: [...state.claims, ...claims] },
    ),
  updateClaim: (id, updates) =>
    set((state) => ({
      claims: state.claims.map((c) => (c.id === id ? { ...c, ...updates } : c)),
      activeClaim:
        state.activeClaim?.id === id
          ? { ...state.activeClaim, ...updates }
          : state.activeClaim,
    })),
  removeClaim: (id) =>
    set((state) => ({
      claims: state.claims.filter((c) => c.id !== id),
      activeClaim: state.activeClaim?.id === id ? null : state.activeClaim,
    })),
  setActiveClaim: (claim) => set({ activeClaim: claim }),
  setValidationStatus: (validationStatus) => set({ validationStatus }),
  setValidationError: (validationError) => set({ validationError }),
  setIdentificationTotalChunks: (identificationTotalChunks) =>
    set({ identificationTotalChunks }),
  setIdentificationChunksCompleted: (identificationChunksCompleted) =>
    set({ identificationChunksCompleted }),

  reset: () => set({ ...initialState }),
}));
